import { FormFieldMessage } from './FormFieldMessage';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CallType, CallFormData } from '@/hooks/useCallCreationFlow';
import { Calendar, DollarSign, FileText, Users, CheckCircle2, X, Check, Search } from 'lucide-react';
import { useState } from 'react';
import type { UserChannel } from '@/hooks/useUserChannels';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

// Channel Selection Message
interface ChannelSelectionMessageProps {
  channels: UserChannel[];
  onSelect: (channelId: string) => void;
  disabled?: boolean;
}

export const ChannelSelectionMessage = ({ channels, onSelect, disabled }: ChannelSelectionMessageProps) => {
  const [selected, setSelected] = useState('');

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium">Select Channel</p>
        <p className="text-sm text-muted-foreground">Choose the channel for this Call</p>
        <RadioGroup value={selected} onValueChange={setSelected} disabled={disabled}>
          {channels.map((channel) => (
            <div key={channel.channel_id} className="flex items-center space-x-2 p-2 rounded hover:bg-accent/50">
              <RadioGroupItem value={channel.channel_id} id={channel.channel_id} />
              <Label htmlFor={channel.channel_id} className="cursor-pointer flex-1">
                {channel.channels.name} - {channel.organization_name}
              </Label>
            </div>
          ))}
        </RadioGroup>
        <Button onClick={() => selected && onSelect(selected)} disabled={!selected || disabled} className="w-full">
          Continue
        </Button>
      </div>
    </FormFieldMessage>
  );
};

// Domain Selection Message
interface DomainSelectionMessageProps {
  domains: Array<{ id: string; name: string; description?: string }>;
  onSelect: (domainId: string) => void;
  disabled?: boolean;
}

export const DomainSelectionMessage = ({ domains, onSelect, disabled }: DomainSelectionMessageProps) => {
  const [selected, setSelected] = useState('');

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium">Select Domain</p>
        <p className="text-sm text-muted-foreground">Choose the functional domain for this Call</p>
        <Select value={selected} onValueChange={setSelected} disabled={disabled}>
          <SelectTrigger>
            <SelectValue placeholder="Select a domain..." />
          </SelectTrigger>
          <SelectContent>
            {domains.map((domain) => (
              <SelectItem key={domain.id} value={domain.id}>
                {domain.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={() => selected && onSelect(selected)} disabled={!selected || disabled} className="w-full">
          Continue
        </Button>
      </div>
    </FormFieldMessage>
  );
};

// Subdomain Selection Message
interface SubdomainSelectionMessageProps {
  subdomains: Array<{ id: string; name: string; description?: string }>;
  onSelect: (subdomainId: string) => void;
  disabled?: boolean;
}

export const SubdomainSelectionMessage = ({ subdomains, onSelect, disabled }: SubdomainSelectionMessageProps) => {
  const [selected, setSelected] = useState('');

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium">Select Subdomain (Optional)</p>
        <p className="text-sm text-muted-foreground">Narrow down the specific area if applicable</p>
        <Select value={selected} onValueChange={setSelected} disabled={disabled}>
          <SelectTrigger>
            <SelectValue placeholder="Select a subdomain..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="skip">Skip - No specific subdomain</SelectItem>
            {subdomains.map((subdomain) => (
              <SelectItem key={subdomain.id} value={subdomain.id}>
                {subdomain.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={() => selected && onSelect(selected)} disabled={!selected || disabled} className="w-full">
          Continue
        </Button>
      </div>
    </FormFieldMessage>
  );
};

// CfK Connection Message
interface CfKConnectionMessageProps {
  onSelect: (connected: boolean) => void;
  disabled?: boolean;
}

export const CfKConnectionMessage = ({ onSelect, disabled }: CfKConnectionMessageProps) => {
  const [selected, setSelected] = useState('');

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium">Connected to Call for Solution?</p>
        <p className="text-sm text-muted-foreground">Is this knowledge supporting a specific CfS?</p>
        <RadioGroup value={selected} onValueChange={setSelected} disabled={disabled}>
          <div className="flex items-center space-x-2 p-2 rounded hover:bg-accent/50">
            <RadioGroupItem value="yes" id="cfk-yes" />
            <Label htmlFor="cfk-yes" className="cursor-pointer">Yes, connected to a CfS</Label>
          </div>
          <div className="flex items-center space-x-2 p-2 rounded hover:bg-accent/50">
            <RadioGroupItem value="no" id="cfk-no" />
            <Label htmlFor="cfk-no" className="cursor-pointer">No, standalone CfK</Label>
          </div>
        </RadioGroup>
        <Button onClick={() => selected && onSelect(selected === 'yes')} disabled={!selected || disabled} className="w-full">
          Continue
        </Button>
      </div>
    </FormFieldMessage>
  );
};

// CfK Bounty Connection Message
interface CfKBountyConnectionMessageProps {
  onSelect: (hasBounty: boolean) => void;
  disabled?: boolean;
}

export const CfKBountyConnectionMessage = ({ onSelect, disabled }: CfKBountyConnectionMessageProps) => {
  const [selected, setSelected] = useState('');

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium">Does the CfS have a Solution Bounty?</p>
        <p className="text-sm text-muted-foreground">This affects attribution and rewards for contributors</p>
        <RadioGroup value={selected} onValueChange={setSelected} disabled={disabled}>
          <div className="flex items-center space-x-2 p-2 rounded hover:bg-accent/50">
            <RadioGroupItem value="yes" id="bounty-yes" />
            <Label htmlFor="bounty-yes" className="cursor-pointer">Yes, CfS has a bounty</Label>
          </div>
          <div className="flex items-center space-x-2 p-2 rounded hover:bg-accent/50">
            <RadioGroupItem value="no" id="bounty-no" />
            <Label htmlFor="bounty-no" className="cursor-pointer">No bounty on CfS</Label>
          </div>
        </RadioGroup>
        <Button onClick={() => selected && onSelect(selected === 'yes')} disabled={!selected || disabled} className="w-full">
          Continue
        </Button>
      </div>
    </FormFieldMessage>
  );
};

const functionalAreas = [
  { id: 'technical-architecture', name: 'Technical Architecture' },
  { id: 'web3-integration', name: 'Web3 Integration' },
  { id: 'user-experience', name: 'User Experience (UX/UI)' },
  { id: 'compliance-governance', name: 'Compliance & Governance' },
  { id: 'ai-automation', name: 'AI & Automation' },
  { id: 'knowledge-management', name: 'Knowledge Management' },
  { id: 'branding', name: 'Branding' },
  { id: 'value-proposition', name: 'Value Proposition' }
];

interface CallTypeMessageProps {
  onSelect: (type: CallType) => void;
  disabled?: boolean;
}

export const CallTypeMessage = ({ onSelect, disabled }: CallTypeMessageProps) => {
  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium">What type of Call would you like to create?</p>
        <RadioGroup onValueChange={(value) => onSelect(value as CallType)} disabled={disabled}>
          <div className="flex items-start space-x-3 p-3 rounded-lg border border-border/50 hover:bg-accent/20 transition-colors">
            <RadioGroupItem value="solution" id="solution" className="mt-1" />
            <Label htmlFor="solution" className="cursor-pointer flex-1">
              <div className="font-medium">Call for Solution</div>
              <div className="text-sm text-muted-foreground">Request innovative solutions to challenges</div>
            </Label>
          </div>
          <div className="flex items-start space-x-3 p-3 rounded-lg border border-border/50 hover:bg-accent/20 transition-colors">
            <RadioGroupItem value="knowledge" id="knowledge" className="mt-1" />
            <Label htmlFor="knowledge" className="cursor-pointer flex-1">
              <div className="font-medium">Call for Knowledge</div>
              <div className="text-sm text-muted-foreground">Gather expertise and insights</div>
            </Label>
          </div>
          <div className="flex items-start space-x-3 p-3 rounded-lg border border-border/50 hover:bg-accent/20 transition-colors">
            <RadioGroupItem value="destruction" id="destruction" className="mt-1" />
            <Label htmlFor="destruction" className="cursor-pointer flex-1">
              <div className="font-medium">Call for Destruction</div>
              <div className="text-sm text-muted-foreground">Identify issues or inefficiencies</div>
            </Label>
          </div>
        </RadioGroup>
      </div>
    </FormFieldMessage>
  );
};

interface FunctionalAreaMessageProps {
  onSelect: (area: string) => void;
  callType: CallType;
  disabled?: boolean;
}

export const FunctionalAreaMessage = ({ onSelect, callType, disabled }: FunctionalAreaMessageProps) => {
  const callTypeLabel = callType === 'solution' ? 'Solution' : callType === 'knowledge' ? 'Knowledge' : 'Destruction';
  
  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium">Great choice! Now select the functional area for your Call for {callTypeLabel}:</p>
        <Select onValueChange={onSelect} disabled={disabled}>
          <SelectTrigger>
            <SelectValue placeholder="Choose a functional area..." />
          </SelectTrigger>
          <SelectContent>
            {functionalAreas.map(area => (
              <SelectItem key={area.id} value={area.id}>
                {area.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </FormFieldMessage>
  );
};

interface BountyMessageProps {
  onSelect: (isBounty: boolean) => void;
  disabled?: boolean;
}

export const BountyMessage = ({ onSelect, disabled }: BountyMessageProps) => {
  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          Would you like to add a bounty to incentivize contributions?
        </p>
        <RadioGroup onValueChange={(value) => onSelect(value === 'yes')} disabled={disabled}>
          <div className="flex items-center space-x-3 p-3 rounded-lg border border-border/50 hover:bg-accent/20 transition-colors">
            <RadioGroupItem value="yes" id="bounty-yes" />
            <Label htmlFor="bounty-yes" className="cursor-pointer">Yes, add a bounty</Label>
          </div>
          <div className="flex items-center space-x-3 p-3 rounded-lg border border-border/50 hover:bg-accent/20 transition-colors">
            <RadioGroupItem value="no" id="bounty-no" />
            <Label htmlFor="bounty-no" className="cursor-pointer">No bounty</Label>
          </div>
        </RadioGroup>
      </div>
    </FormFieldMessage>
  );
};

interface BountyDetailsMessageProps {
  onSubmit: (amount: string, deadline: string) => void;
  disabled?: boolean;
}

export const BountyDetailsMessage = ({ onSubmit, disabled }: BountyDetailsMessageProps) => {
  const [amount, setAmount] = useState('');
  const [deadline, setDeadline] = useState('');

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium">Please provide the bounty details:</p>
        <div className="space-y-3">
          <div>
            <Label htmlFor="bounty-amount" className="flex items-center gap-2 mb-2">
              <DollarSign className="h-4 w-4" />
              Bounty Amount
            </Label>
            <Input
              id="bounty-amount"
              type="number"
              placeholder="Enter amount..."
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={disabled}
            />
          </div>
          <div>
            <Label htmlFor="bounty-deadline" className="flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4" />
              Deadline
            </Label>
            <Input
              id="bounty-deadline"
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              disabled={disabled}
            />
          </div>
          <Button
            onClick={() => onSubmit(amount, deadline)}
            disabled={!amount || !deadline || disabled}
            className="w-full"
          >
            Continue
          </Button>
        </div>
      </div>
    </FormFieldMessage>
  );
};

interface CallDetailsMessageProps {
  onSubmit: (title: string, description: string, requirements: string) => void;
  disabled?: boolean;
}

export const CallDetailsMessage = ({ onSubmit, disabled }: CallDetailsMessageProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [requirements, setRequirements] = useState('');

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Now let's define the details of your Call:
        </p>
        <div className="space-y-3">
          <div>
            <Label htmlFor="call-title">Title</Label>
            <Input
              id="call-title"
              placeholder="Enter a clear, concise title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={disabled}
            />
          </div>
          <div>
            <Label htmlFor="call-description">Description</Label>
            <Textarea
              id="call-description"
              placeholder="Describe the challenge or objective..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={disabled}
              rows={4}
            />
          </div>
          <div>
            <Label htmlFor="call-requirements">Requirements</Label>
            <Textarea
              id="call-requirements"
              placeholder="What are the key requirements or criteria?"
              value={requirements}
              onChange={(e) => setRequirements(e.target.value)}
              disabled={disabled}
              rows={3}
            />
          </div>
          <Button
            onClick={() => onSubmit(title, description, requirements)}
            disabled={!title || !description || disabled}
            className="w-full"
          >
            Continue
          </Button>
        </div>
      </div>
    </FormFieldMessage>
  );
};

interface RolesMessageProps {
  onSubmit: (curator: string, confirmer: string, contributors: string[]) => void;
  disabled?: boolean;
}

interface PlatformUser {
  id: string;
  display_name: string;
  username: string | null;
  avatar_url: string | null;
  email: string;
}

// Single-select member picker component
const SingleMemberSelect = ({ 
  label, 
  placeholder,
  selectedUserId,
  onUserSelect,
  onUserClear,
  manualEmail,
  onManualEmailChange,
  platformUsers,
  disabled,
  isLoading
}: {
  label: string;
  placeholder: string;
  selectedUserId: string | null;
  onUserSelect: (userId: string) => void;
  onUserClear: () => void;
  manualEmail: string;
  onManualEmailChange: (email: string) => void;
  platformUsers: PlatformUser[];
  disabled?: boolean;
  isLoading?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [useManualEmail, setUseManualEmail] = useState(false);
  const selectedUser = platformUsers.find(u => u.id === selectedUserId);

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      
      {!useManualEmail ? (
        <>
          {selectedUser ? (
            <div className="flex items-center gap-2 p-2 rounded-md border border-border bg-accent/20">
              <Avatar className="h-6 w-6">
                <AvatarImage src={selectedUser.avatar_url || ''} />
                <AvatarFallback className="text-xs">
                  {selectedUser.display_name?.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="flex-1 text-sm">{selectedUser.display_name}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={onUserClear}
                disabled={disabled}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between font-normal"
                  disabled={disabled || isLoading}
                >
                  <span className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    {placeholder}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search members..." />
                  <CommandList>
                    <CommandEmpty>No members found.</CommandEmpty>
                    <CommandGroup>
                      {platformUsers.map((user) => (
                        <CommandItem
                          key={user.id}
                          value={user.display_name}
                          onSelect={() => {
                            onUserSelect(user.id);
                            setOpen(false);
                          }}
                        >
                          <Avatar className="h-6 w-6 mr-2">
                            <AvatarImage src={user.avatar_url || ''} />
                            <AvatarFallback className="text-xs">
                              {user.display_name?.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                        <div className="flex flex-col">
                            <span className="text-sm">{user.display_name}</span>
                            <span className="text-xs text-muted-foreground">
                              @{user.username || user.display_name?.toLowerCase().replace(/\s+/g, '-')}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs text-muted-foreground"
            onClick={() => setUseManualEmail(true)}
            disabled={disabled}
          >
            Or enter email manually
          </Button>
        </>
      ) : (
        <>
          <Input
            type="email"
            placeholder="email@example.com"
            value={manualEmail}
            onChange={(e) => onManualEmailChange(e.target.value)}
            disabled={disabled}
          />
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs text-muted-foreground"
            onClick={() => setUseManualEmail(false)}
            disabled={disabled}
          >
            Or select from platform members
          </Button>
        </>
      )}
    </div>
  );
};

// Multi-select member picker component
const MultiMemberSelect = ({ 
  label, 
  placeholder,
  selectedUserIds,
  onUserToggle,
  manualEmails,
  onManualEmailsChange,
  platformUsers,
  disabled,
  isLoading
}: {
  label: string;
  placeholder: string;
  selectedUserIds: string[];
  onUserToggle: (userId: string) => void;
  manualEmails: string;
  onManualEmailsChange: (emails: string) => void;
  platformUsers: PlatformUser[];
  disabled?: boolean;
  isLoading?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const selectedUsers = platformUsers.filter(u => selectedUserIds.includes(u.id));

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      
      {/* Selected users display */}
      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-1 p-2 rounded-md border border-border bg-accent/20">
          {selectedUsers.map((user) => (
            <div key={user.id} className="flex items-center gap-1 bg-background rounded-full px-2 py-1">
              <Avatar className="h-4 w-4">
                <AvatarImage src={user.avatar_url || ''} />
                <AvatarFallback className="text-[10px]">
                  {user.display_name?.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs">{user.display_name}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0"
                onClick={() => onUserToggle(user.id)}
                disabled={disabled}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
      
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
            disabled={disabled || isLoading}
          >
            <span className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              {placeholder}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search members..." />
            <CommandList>
              <CommandEmpty>No members found.</CommandEmpty>
              <CommandGroup>
                {platformUsers.map((user) => {
                  const isSelected = selectedUserIds.includes(user.id);
                  return (
                    <CommandItem
                      key={user.id}
                      value={user.display_name}
                      onSelect={() => onUserToggle(user.id)}
                    >
                      <div className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                        isSelected ? "bg-primary text-primary-foreground" : "opacity-50"
                      )}>
                        {isSelected && <Check className="h-3 w-3" />}
                      </div>
                      <Avatar className="h-6 w-6 mr-2">
                        <AvatarImage src={user.avatar_url || ''} />
                        <AvatarFallback className="text-xs">
                          {user.display_name?.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="text-sm">{user.display_name}</span>
                        <span className="text-xs text-muted-foreground">
                          @{user.username || user.display_name?.toLowerCase().replace(/\s+/g, '-')}
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Or add by email (comma-separated)</Label>
        <Input
          type="text"
          placeholder="contributor1@example.com, contributor2@example.com"
          value={manualEmails}
          onChange={(e) => onManualEmailsChange(e.target.value)}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export const RolesMessage = ({ onSubmit, disabled }: RolesMessageProps) => {
  const { user } = useAuth();
  
  // State for curator
  const [curatorUserId, setCuratorUserId] = useState<string | null>(null);
  const [curatorEmail, setCuratorEmail] = useState('');
  
  // State for confirmer
  const [confirmerUserId, setConfirmerUserId] = useState<string | null>(null);
  const [confirmerEmail, setConfirmerEmail] = useState('');
  
  // State for contributors
  const [contributorUserIds, setContributorUserIds] = useState<string[]>([]);
  const [contributorEmails, setContributorEmails] = useState('');

  // Fetch platform users
  const { data: platformUsers = [], isLoading } = useQuery({
    queryKey: ['platform-users-for-roles', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id, display_name, username, avatar_url, email')
        .order('display_name');
      return (data?.filter(p => p.id !== user?.id) || []) as PlatformUser[];
    },
    enabled: !!user?.id
  });

  const handleContributorToggle = (userId: string) => {
    setContributorUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSubmit = () => {
    // Get curator display value - use @username for platform users, email for manual entry
    const curatorUser = curatorUserId ? platformUsers.find(u => u.id === curatorUserId) : null;
    const curatorValue = curatorUser 
      ? `@${curatorUser.username || curatorUser.display_name?.toLowerCase().replace(/\s+/g, '-')}`
      : curatorEmail;
    
    // Get confirmer display value - use @username for platform users, email for manual entry
    const confirmerUser = confirmerUserId ? platformUsers.find(u => u.id === confirmerUserId) : null;
    const confirmerValue = confirmerUser
      ? `@${confirmerUser.username || confirmerUser.display_name?.toLowerCase().replace(/\s+/g, '-')}`
      : confirmerEmail;
    
    // Get contributors - use @username for platform users, email for manual entries
    const contributorValues = [
      ...contributorUserIds.map(id => {
        const user = platformUsers.find(u => u.id === id);
        return user ? `@${user.username || user.display_name?.toLowerCase().replace(/\s+/g, '-')}` : null;
      }).filter(Boolean),
      ...contributorEmails.split(',').map(e => e.trim()).filter(Boolean)
    ] as string[];
    
    onSubmit(curatorValue, confirmerValue, contributorValues);
  };

  const hasCurator = curatorUserId || curatorEmail;

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium flex items-center gap-2">
          <Users className="h-4 w-4" />
          Finally, let's assign roles for this Call:
        </p>
        <div className="space-y-4">
          <SingleMemberSelect
            label="Creator (Leader)"
            placeholder="Search platform members..."
            selectedUserId={curatorUserId}
            onUserSelect={setCuratorUserId}
            onUserClear={() => setCuratorUserId(null)}
            manualEmail={curatorEmail}
            onManualEmailChange={setCuratorEmail}
            platformUsers={platformUsers}
            disabled={disabled}
            isLoading={isLoading}
          />
          
          <SingleMemberSelect
            label="Confirmer"
            placeholder="Search platform members..."
            selectedUserId={confirmerUserId}
            onUserSelect={setConfirmerUserId}
            onUserClear={() => setConfirmerUserId(null)}
            manualEmail={confirmerEmail}
            onManualEmailChange={setConfirmerEmail}
            platformUsers={platformUsers}
            disabled={disabled}
            isLoading={isLoading}
          />
          
          <MultiMemberSelect
            label="Contributors"
            placeholder="Add contributors..."
            selectedUserIds={contributorUserIds}
            onUserToggle={handleContributorToggle}
            manualEmails={contributorEmails}
            onManualEmailsChange={setContributorEmails}
            platformUsers={platformUsers}
            disabled={disabled}
            isLoading={isLoading}
          />
          
          <Button
            onClick={handleSubmit}
            disabled={!hasCurator || disabled}
            className="w-full"
          >
            Review Call
          </Button>
        </div>
      </div>
    </FormFieldMessage>
  );
};

interface ReviewMessageProps {
  formData: CallFormData;
  onConfirm: () => void;
  onEdit: () => void;
  disabled?: boolean;
}

export const ReviewMessage = ({ formData, onConfirm, onEdit, disabled }: ReviewMessageProps) => {
  const callTypeLabel = formData.type === 'solution' ? 'Solution' : formData.type === 'knowledge' ? 'Knowledge' : 'Destruction';
  const functionalAreaLabel = functionalAreas.find(a => a.id === formData.functionalArea)?.name || formData.functionalArea;

  return (
    <FormFieldMessage variant="assistant">
      <div className="space-y-4">
        <p className="font-medium flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" />
          Please review your Call before submitting:
        </p>
        <Card className="p-4 space-y-3 bg-accent/20">
          <div>
            <div className="text-xs text-muted-foreground">Type</div>
            <div className="font-medium">Call for {callTypeLabel}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Functional Area</div>
            <div className="font-medium">{functionalAreaLabel}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Title</div>
            <div className="font-medium">{formData.title}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Description</div>
            <div className="text-sm">{formData.description}</div>
          </div>
          {formData.isBounty && (
            <div>
              <div className="text-xs text-muted-foreground">Bounty</div>
              <div className="font-medium">${formData.bountyAmount} • Deadline: {formData.deadline}</div>
            </div>
          )}
          <div>
            <div className="text-xs text-muted-foreground">Creator (Leader)</div>
            <div className="text-sm">{formData.curator}</div>
          </div>
          {formData.confirmer && (
            <div>
              <div className="text-xs text-muted-foreground">Confirmer</div>
              <div className="text-sm">{formData.confirmer}</div>
            </div>
          )}
          {formData.contributors && formData.contributors.length > 0 && (
            <div>
              <div className="text-xs text-muted-foreground">Contributors</div>
              <div className="text-sm">{formData.contributors.join(', ')}</div>
            </div>
          )}
        </Card>
        <div className="flex gap-2">
          <Button onClick={onEdit} variant="outline" className="flex-1" disabled={disabled}>
            Edit
          </Button>
          <Button onClick={onConfirm} className="flex-1" disabled={disabled}>
            Submit Call
          </Button>
        </div>
      </div>
    </FormFieldMessage>
  );
};

import React from 'react';
